import AuthenticatedClient from '@/repositories/clients/AuthenticatedClient';

/**
 * Admin user/tenant management API (`/api/v2/manage/*`, super-users only).
 * Backend proxies these to Scrumble. Returns raw AxiosResponses — read `.data`.
 */
const base = 'manage';

export default {
  me() {
    return AuthenticatedClient.get(`${base}/me`);
  },
  /** HubSpot card for a tenant, user or lead e-mail (`kind`: tenant | user | contact). */
  crmCard(kind, id) {
    if (kind === 'contact') {
      return AuthenticatedClient.get(`${base}/crm/contacts`, { params: { email: id } });
    }
    return AuthenticatedClient.get(`${base}/crm/${kind}s/${encodeURIComponent(id)}`);
  },
  /** Batch id → display label for admin tables. See `useDirectory()`. */
  resolveDirectory({ userIds = [], tenantIds = [] } = {}) {
    return AuthenticatedClient.post(`${base}/directory/resolve`, {
      user_ids: userIds,
      tenant_ids: tenantIds,
    });
  },
  users({ page = 1, perPage = 25, search = '', sortField = 'created_at', sortDir = 'desc' } = {}) {
    return AuthenticatedClient.get(`${base}/users`, {
      params: { page, per_page: perPage, search, sort_field: sortField, sort_dir: sortDir },
    });
  },
  user(slug) {
    return AuthenticatedClient.get(`${base}/users/${slug}`);
  },
  userDetail(slug) {
    return AuthenticatedClient.get(`${base}/users/${slug}/detail`);
  },
  invite(payload) {
    return AuthenticatedClient.post(`${base}/users/invite`, payload);
  },
  previewInvitation(payload) {
    return AuthenticatedClient.post(`${base}/invitations/preview`, payload);
  },
  invitations(params = {}) {
    return AuthenticatedClient.get(`${base}/invitations`, { params });
  },
  invitationHistory(params) {
    return AuthenticatedClient.get(`${base}/invitations/history`, { params });
  },
  resendInvitation(id, payload) {
    return AuthenticatedClient.post(`${base}/invitations/${id}/resend`, payload);
  },
  cancelInvitation(id, payload) {
    return AuthenticatedClient.post(`${base}/invitations/${id}/cancel`, payload);
  },
  resendMemberInvitation(id, payload) {
    return AuthenticatedClient.post(`${base}/invitations/members/${id}/resend`, payload);
  },
  cancelMemberInvitation(id, payload) {
    return AuthenticatedClient.post(`${base}/invitations/members/${id}/cancel`, payload);
  },
  update(slug, payload) {
    return AuthenticatedClient.put(`${base}/users/${slug}`, payload);
  },
  remove(slug) {
    return AuthenticatedClient.delete(`${base}/users/${slug}`);
  },
  tenants({ withSingleUser = false } = {}) {
    return AuthenticatedClient.get(`${base}/tenants`, {
      params: { with_single_user: withSingleUser },
    });
  },
  tenantsPaged({
    page = 1,
    perPage = 25,
    search = '',
    sortField = 'created_at',
    sortDir = 'desc',
    withSingleUser = false,
  } = {}) {
    return AuthenticatedClient.get(`${base}/tenants/table`, {
      params: {
        page,
        per_page: perPage,
        search,
        sort_field: sortField,
        sort_dir: sortDir,
        with_single_user: withSingleUser,
      },
    });
  },
  tenant(id) {
    return AuthenticatedClient.get(`${base}/tenants/${id}`);
  },
  members(tenantId, { search = '', page = 1 } = {}) {
    return AuthenticatedClient.get(`${base}/tenants/${tenantId}/users`, {
      params: { search, page },
    });
  },
  tenantDetail(id) {
    return AuthenticatedClient.get(`${base}/tenants/${id}/detail`);
  },
  createTenant(payload) {
    return AuthenticatedClient.post(`${base}/tenants`, payload);
  },
  updateTenant(id, payload) {
    return AuthenticatedClient.put(`${base}/tenants/${id}`, payload);
  },
  /** MRR invoiced outside Stripe (EUR/month); `null` clears it. */
  setManualMrr(id, manualMrrEur) {
    return AuthenticatedClient.put(`${base}/tenants/${id}/manual-mrr`, {
      manual_mrr_eur: manualMrrEur,
    });
  },
  reviewRestriction(id, payload) {
    return AuthenticatedClient.post(`${base}/tenants/${id}/restrictions/review`, payload);
  },
  applyRestriction(id, payload) {
    return AuthenticatedClient.post(`${base}/tenants/${id}/restrictions`, payload);
  },
  restrictionReceipt(id, key) {
    return AuthenticatedClient.get(`${base}/tenants/${id}/restrictions/${key}`);
  },
  restrictionHistory(id, page = 1) {
    return AuthenticatedClient.get(`${base}/tenants/${id}/restrictions`, { params: { page } });
  },
  removeTenant(id) {
    return AuthenticatedClient.delete(`${base}/tenants/${id}`);
  },
};
